import {Component, Inject, OnDestroy} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {Package} from '../../../../../model/Package';

@Component({
  selector: 'app-package-override-dialog',
  template: `
    <h2 mat-dialog-title>
      <mat-icon [class]="level === 'shipped' ? 'override-icon-shipped' : 'override-icon-label'">
        {{ level === 'shipped' ? 'warning' : 'info' }}
      </mat-icon>
      <span *ngIf="level === 'shipped'" i18n="@@Package Already Shipped">Package Already Shipped</span>
      <span *ngIf="level === 'label'" i18n="@@Labels Already Generated">Labels Already Generated</span>
    </h2>
    <mat-dialog-content>
      <div *ngIf="level === 'shipped'" class="override-warning-shipped">
        <p i18n="@@shipped override warning">
          <strong>WARNING:</strong> Package #{{pkg.package_ID}} has already been shipped.
          Modifying a shipped package can cause tracking inconsistencies and billing issues.
          This action should only be taken in exceptional circumstances.
        </p>
        <p *ngIf="armed" class="override-arm-hint" i18n="@@Press the button once more to apply this override">
          Press the button once more to apply this override.
        </p>
      </div>
      <div *ngIf="level === 'label'" class="override-warning-label">
        <p i18n="@@label override warning">
          Package #{{pkg.package_ID}} has labels that have already been generated.
          Modifying this package will invalidate those labels and they will need to be regenerated.
        </p>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="onCancel()" i18n="dialog dismissal|Button that closes a dialog and discards any unsaved edits. It never removes or destroys existing data - the Delete button does that. Must translate to a word meaning 'abandon this action', not 'erase'.@@Cancel">Cancel</button>
      <button mat-raised-button color="warn" (click)="onConfirm()"
              [disabled]="armed && !canConfirm">
        <span *ngIf="!armed" i18n="@@Override">Override</span>
        <span *ngIf="armed" i18n="@@Confirm Override">Confirm Override</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .override-icon-shipped { color: #d32f2f; margin-inline-end: 8px; vertical-align: middle; }
    .override-icon-label { color: #f57c00; margin-inline-end: 8px; vertical-align: middle; }
    .override-warning-shipped { background: #fde8e8; border-left: 4px solid #d32f2f; padding: 12px 16px; border-radius: 4px; }
    .override-warning-shipped p { color: #b71c1c; margin: 0 0 12px 0; }
    .override-warning-label { background: #fff3e0; border-left: 4px solid #f57c00; padding: 12px 16px; border-radius: 4px; }
    .override-warning-label p { color: #e65100; margin: 0; }
    .override-arm-hint { font-weight: 600; margin: 0 !important; }
  `]
})
export class PackageOverrideDialogComponent implements OnDestroy {
  level: 'label' | 'shipped';
  pkg: Package;

  /**
   * Confirmation for the `shipped` level is a SECOND deliberate press of
   * the Override button rather than typing a magic word.
   *
   * The previous gate asked the operator to type `OVERRIDE` into a text
   * box. That is hostile to every non-English operator: the literal
   * string compared against was `'OVERRIDE'`, so the check could only
   * ever be satisfied by typing an English word, no matter what the
   * localized placeholder told them to type. The placeholder was
   * translated; the comparison was not. In Arabic the instruction read
   * "اكتب OVERRIDE للتأكيد" and the only way through was to switch
   * keyboard layouts.
   *
   * A second press carries the same "stop and think" weight, works in
   * every locale and on touch devices, and has nothing to mistranslate.
   */
  armed = false;

  /**
   * Guards against a physical double-click sailing straight through
   * both presses. Without this the new gate would be weaker than a
   * single click, because one accidental double-tap would arm AND
   * confirm in the same gesture. The button stays disabled for
   * ARM_DEBOUNCE_MS after arming.
   */
  canConfirm = false;

  private static readonly ARM_DEBOUNCE_MS = 600;
  private armTimer: any = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dialogRef: MatDialogRef<PackageOverrideDialogComponent>
  ) {
    this.level = data.level;
    this.pkg = data.pkg;
  }

  ngOnDestroy(): void {
    if (this.armTimer) {
      clearTimeout(this.armTimer);
      this.armTimer = null;
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    // `label` level was always a single confirmation - regenerating a
    // label is recoverable, so it does not earn the extra friction.
    if (this.level !== 'shipped') {
      this.dialogRef.close(true);
      return;
    }
    if (!this.armed) {
      this.armed = true;
      this.canConfirm = false;
      this.armTimer = setTimeout(() => {
        this.canConfirm = true;
        this.armTimer = null;
      }, PackageOverrideDialogComponent.ARM_DEBOUNCE_MS);
      return;
    }
    if (!this.canConfirm) {
      return;
    }
    this.dialogRef.close(true);
  }
}

